import React, { useEffect, useMemo, useState } from 'react';
import { Button, Container, Dropdown, Form, Modal, ProgressBar } from 'react-bootstrap';
import { toast } from 'sonner';
import FormGenerator from '../../form-generator/FormGenerator';
import { makeRESTApiRequest } from '../../helpers/api';
import {
  ADMIN_ROLE,
  BUTTONS_FOR_DIFFERENT_ENTITIES,
  enableDisableOrDelete,
  ENDPOINTS,
  ORG_ROLES,
  SALES_PERSON_ROLE
} from '../../helpers/constants';
import {
  findFilterObjectForAdmin,
  isOrgAdmin,
  isUserOrOrganizationEntity,
  onCreateApiRequestData,
  onUpdateApiRequestData,
  onUpdateStatusOrDeleteApiRequestData,
  setItemsForEntityCrud
} from '../../helpers/global';
import AlertModal from '../AlertModal';
import Loader from '../Loader';
import EntityTable from './EntityTable';
import UsersCardView from '../admin/UsersCardView';
import UserFormModal from '../Users/UserFormModal';
import OrganizationFormModal from '../Organizations/OrganizationFormModal';
import useAppChoices from '../../hooks/useAppChoices';
import { Ellipsis, Plus } from 'lucide-react';
import { UsersCardLoader } from '../UsersCardLoader';

const getForm = ({ entityName, rows, type = 'Add' }) => ({
  forms: [
    {
      name: `${type} ${entityName?.toLowerCase()}`,
      markCompulsoryFields: true,
      hideFormName: true,
      compact: true,
      submit: {
        name: type === 'Add' ? 'Invite' : 'Update',
        show: true,
        onSubmit: type === 'Add' ? 'onNewItemFormSubmit' : 'onUpdateItemFormSubmit'
      },
      rows
    }
  ]
});

export const AddEditModal = ({ show, toBeEditedItem, onHide, showProgress, addForm, updateForm, entityName }) => {
  return (
    <Modal size="lg" show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton={!showProgress}>
        <Modal.Title>
          <h6 className="mb-0">{toBeEditedItem ? `Update ${entityName}` : `Invite ${entityName}`}</h6>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-auto px-1">
        <FormGenerator
          formJson={toBeEditedItem ? updateForm : addForm}
          formValues={
            toBeEditedItem
              ? {
                [`Update ${entityName.toLowerCase()}`]: {
                  ...toBeEditedItem,
                  isEnabled: toBeEditedItem?.isEnabled ? 'Enable' : 'Disable'
                }
              }
              : {}
          }
        />

        {showProgress && (
          <ProgressBar
            variant="dark"
            now={100}
            animated
            striped
            label={toBeEditedItem ? `Updating ${entityName}...` : `Saving ${entityName}....`}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

/**
 *  example: 
 *  entityName: Manufacturer,
 *  entityKey: manufacturer,
    endpoint: /manufacturers
    formFields: 
 */
const EntityCRUD = ({
  entityName,
  updateEndpoint,
  endpoint,
  entityTableHeaders,
  entityTableSearchKeys,
  addFormRows = [],
  updateFormRows = [],
  onProgressChange,
  onEntityUpdate,
  entityAppChoiceKey,
  onUsersFromOrganizationChange,
  usersFromOrganization,
  isEnabledFilter = false
}) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [toUpdateStatusOrDelete, setToUpdateStatusOrDelete] = useState(null);
  const [itemActionMeta, setItemActionMeta] = useState(null);
  const [itemAddingUpdating, setItemAddingUpdating] = useState(false);
  const [reinviting, setReinviting] = useState(false);
  const [userToReinvite, setUserToReinvite] = useState(null);
  const [enabledUsersOnly, setEnabledUsersOnly] = useState(true);
  const [role] = useState(localStorage.getItem('user-role'));
  const [user] = useState(JSON.parse(localStorage.getItem('user')));
  const usersForOrg = useAppChoices('users') || [];
  const requiredUsers = usersForOrg.filter(u => u.role === ADMIN_ROLE || u.role === 'Processor' || u.role === SALES_PERSON_ROLE);

  const shouldEnableFilter = useMemo(() => {
    return isEnabledFilter || entityName === 'Users';
  }, [isEnabledFilter, entityName]);

  const fetchItems = async () => {
    setLoading(true);
    const baseFilter = isUserOrOrganizationEntity(entityName)
      ? findFilterObjectForAdmin(usersFromOrganization, entityName, role, user?.organization)
      : {};

    const filter = shouldEnableFilter ? { ...baseFilter, isEnabled: enabledUsersOnly ? true : undefined } : baseFilter;

    const { response, error } = await makeRESTApiRequest({
      endpoint: `${endpoint}/search`,
      requestBody: {
        filter: filter,
        sort: { name: 1 }
      }
    });
    setLoading(false);
    if (error) {
      toast.error(error);
      return;
    }
    setItems(setItemsForEntityCrud(entityName, response, user)); // entityName and response are cumpolsary Parameters
  };

  useEffect(() => {
    // Reset items when switching entities
    setItems([]);
    setItemActionMeta(null);
    setToUpdateStatusOrDelete(null);
    fetchItems();
  }, [endpoint, entityName, usersFromOrganization, enabledUsersOnly]);

  // useEffect(() => {
  //   if (!items) return;
  //   //update localstorage
  //   const oldAppChoicesString = localStorage.getItem('app-choices');
  //   let appChoices = [];
  //   try {
  //     appChoices = JSON.parse(oldAppChoicesString);
  //   } catch (e) {}

  //   const oldAppChoiceIndex = appChoices?.findIndex(ac => ac?.key === entityAppChoiceKey);
  //   if (oldAppChoiceIndex === -1) {
  //     appChoices.push({ key: entityAppChoiceKey, values: items });
  //   } else {
  //     //   appChoices[oldAppChoiceIndex] = { key: entityAppChoiceKey, values: items };
  //   }

  //   localStorage.setItem('app-choices', JSON.stringify(appChoices));
  // }, [items]);

  useEffect(() => {
    onProgressChange && onProgressChange(deleteInProgress || itemAddingUpdating);
  }, [deleteInProgress, itemAddingUpdating]);

  // For Users or Organizations Entity Deletion Means Disabling or Enabling That Particular Entity

  const updateStatusOrDeleteItem = async () => {
    setDeleteInProgress(true);

    const { response, error } = await makeRESTApiRequest(
      onUpdateStatusOrDeleteApiRequestData(entityName, updateEndpoint, toUpdateStatusOrDelete, endpoint)
    ); // All Parameters Are compulsory

    setDeleteInProgress(false);
    if (error) {
      toast.error(error);
      return;
    }

    // Update Logic For Users and Organizations Entity

    // Mode With Be Either Delete Or ED(Enable Disable)

    if (toUpdateStatusOrDelete?.mode === 'ED') {
      const itemIndex = items.findIndex(i => i._id === response?._id);
      items[itemIndex] = { ...items[itemIndex], ...response };
      setItems([...items]);
    }
    if (toUpdateStatusOrDelete?.mode === 'Delete') {
      // For Local Deletion Need To Implement Deletion Logic In Future
    }

    onEntityUpdate && onEntityUpdate(entityAppChoiceKey, entityName, items);
    setToUpdateStatusOrDelete(null);
    toast.success(`${entityName.slice(0, entityName.length - 1)}'s Status Successfully Updated`);
  };

  const handleCustomFormSubmit = async form => {
    if (itemActionMeta?.mode === 'add') {
      await onNewItemFormSubmit(form);
    } else {
      await onUpdateItemFormSubmit(form);
    }
  };

  const onNewItemFormSubmit = async form => {
    setItemAddingUpdating(true);
    const { response, error } = await makeRESTApiRequest(
      onCreateApiRequestData(entityName, endpoint, form, usersFromOrganization, user) // entityName ,endPoint and form Parameters are compulsory
    );

    setItemAddingUpdating(false);
    if (error) {
      toast.error(error);
      return;
    }

    const newItems = [...items, { ...response }];
    setItems(newItems);
    onEntityUpdate && onEntityUpdate(entityAppChoiceKey, entityName, newItems);
    setItemActionMeta(null);
    toast.success(`${entityName.slice(0, entityName.length - 1)} Successfully Added`);
  };

  const onUpdateItemFormSubmit = async form => {
    setItemAddingUpdating(true);
    const { response, error } = await makeRESTApiRequest(
      onUpdateApiRequestData(entityName, updateEndpoint, itemActionMeta, form)
    ); // All Pamameters are compulsory
    setItemAddingUpdating(false);
    if (error) {
      toast.error(error);
      return;
    }

    const itemIndex = items.findIndex(i => i._id === response?._id);
    items[itemIndex] = { ...items[itemIndex], ...response };
    setItems([...items]);
    onEntityUpdate && onEntityUpdate(entityAppChoiceKey, entityName, items);
    setItemActionMeta(null);
    toast.success(`${entityName.slice(0, entityName.length - 1)} Successfully Updated`);
  };

  const handleReinviteClick = user => {
    setUserToReinvite(user);
  };

  const confirmReinviteUser = async () => {
    if (!userToReinvite) return;

    setReinviting(true);
    const requestBody = {
      staff: [{ email: userToReinvite.email, name: userToReinvite.name }]
    };

    // For Admin role, include organizationId if:
    // 1. Viewing users from a specific organization, OR
    // 2. The user being reinvited has an organization (org users)
    // Staff users (Admin/Processor) without organization don't need organizationId
    if (role === ADMIN_ROLE) {
      if (usersFromOrganization?._id) {
        requestBody.organizationId = usersFromOrganization._id;
      } else if (userToReinvite.organization) {
        requestBody.organizationId = userToReinvite.organization._id || userToReinvite.organization;
      }
      // If neither condition is met, don't include organizationId (for staff users)
    }

    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.INVITE_STAFF,
      method: 'POST',
      requestBody
    });
    setReinviting(false);
    if (error) {
      toast.error(error);
      setUserToReinvite(null);
      return;
    }
    const invitedCount = response?.invited?.length || 0;
    if (invitedCount > 0) {
      toast.success(`Invitation sent to ${userToReinvite.name}`);
    } else {
      toast.error('Failed to send invitation');
    }
    setUserToReinvite(null);
  };

  const currentEntityActionButtons = useMemo(() => {
    return (
      BUTTONS_FOR_DIFFERENT_ENTITIES.find(entity => entityName === entity.entityName) ??
      BUTTONS_FOR_DIFFERENT_ENTITIES[BUTTONS_FOR_DIFFERENT_ENTITIES?.length - 1]
    );
  }, [entityName]);

  const actionsOnClick = (item, label) => {
    if (enableDisableOrDelete.includes(label)) {
      const isEnabled = label === 'Enable';
      setToUpdateStatusOrDelete({
        ...item,
        isEnabled,
        mode: label === 'Delete' ? 'Delete' : 'ED'
      });
    }

    if (label === 'Edit') {
      setItemActionMeta({ item, mode: 'edit' });
    }

    if (label === 'Reinvite' && entityName === 'Users') {
      handleReinviteClick(item);
    }

    if (label === 'View Users') {
      onUsersFromOrganizationChange({ ...item });
    }

    if (label === 'View Dashboard') {
      window.open(`/dashboard?organizationId=${item._id}`, '_blank');
    }
  };

  const renderActionButtons = item => (
    <div>
      <Dropdown key={item.name} drop="down-start" autoClose="true">
        <Dropdown.Toggle
          as={Button}
          size="sm"
          variant="outline-light"
          className="p-0 d-flex align-items-center justify-content-center border-0 text-dark dropdown-toggle-no-arrow"
          style={{ width: 28, height: 28 }}
        >
          <Ellipsis size={14} />
        </Dropdown.Toggle>
        <Dropdown.Menu align="start" className="p-1">
          {currentEntityActionButtons?.buttons
            .filter(({ label }) =>
              currentEntityActionButtons?.containsEnableOrDisableMode
                ? !item?.isEnabled
                  ? label !== 'Disable'
                  : label !== 'Enable'
                : true
            )
            .map(({ label, icon }) => {
              return (
                <Dropdown.Item
                  key={label}
                  as={Button}
                  size="sm"
                  variant=""
                  className={`py-2 px-2 mr-1 d-flex align-items-center text-dark`}
                  style={{ borderRadius: '12px' }}
                  onClick={e => {
                    e.preventDefault(); // prevent default dropdown behavior
                    actionsOnClick(item, label);
                  }}
                >
                  {icon} <span className="ml-2">{label}</span>
                </Dropdown.Item>
              );
            })}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );

  window['onNewItemFormSubmit'] = onNewItemFormSubmit;
  window['onUpdateItemFormSubmit'] = onUpdateItemFormSubmit;

  return (
    <Container
      fluid
      className="d-flex flex-column"
      style={{ height: 'calc(100vh - 80px)', overflow: 'hidden', padding: '1rem' }}
    >
      <div className="flex-grow-1 d-flex flex-column px-3 pt-3" style={{ minHeight: 0, overflow: 'hidden' }}>
        <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0, overflow: 'hidden' }}>
          <div className="d-flex align-items-center justify-content-between">
            <div style={{ flexShrink: 0 }}>
              <h6 className="font-weight-bold xxxlargeFont">{entityName}</h6>
              <p className="text-muted midFont">Manage your {entityName.slice(0, entityName.length - 1)}</p>
            </div>

            {shouldEnableFilter && (
              <Form.Check
                inline
                type="checkbox"
                id="enabled-users-only"
                label="Only Enabled Users"
                checked={enabledUsersOnly}
                onChange={e => setEnabledUsersOnly(e.target.checked)}
                style={{ '--checkbox-accent-color': 'var(--bs-primary, #28a745)' }}
                className="custom-step-checkbox flex-shrink-0"
              />
            )}

          </div>
          {loading ? (
            entityName === 'Users' && (isOrgAdmin(role) || usersFromOrganization) ? (
              <UsersCardLoader loading={loading} />
            ) : (
              <Loader />
            )
          ) : (
            items && (
              <>
                {entityName === 'Users' && (ORG_ROLES.includes(role) || usersFromOrganization) ? (
                  <UsersCardView
                    title={entityName}
                    users={items}
                    searchQueryKeys={entityTableSearchKeys}
                    onAddClick={() => setItemActionMeta({ mode: 'add' })}
                    onEditUser={item => setItemActionMeta({ item, mode: 'edit' })}
                    onChangeUserStatus={(item, status) => {
                      const isEnabled = status === 'Enabled';
                      if (!!item.isEnabled === isEnabled) return;
                      setToUpdateStatusOrDelete({ ...item, isEnabled, mode: 'ED' });
                    }}
                    onReinviteUser={item => handleReinviteClick(item)}
                  />
                ) : (
                  <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: 0, overflow: 'hidden' }}>
                    <EntityTable
                      rowKeyField={'_id'}
                      data={items}
                      headers={entityTableHeaders}
                      searchQueryKeys={entityTableSearchKeys}
                      additionalComponents={() => (
                        <Button
                          size="sm"
                          variant="info"
                          onClick={() => setItemActionMeta({ mode: 'add' })}
                          className="d-flex align-items-center"
                          style={{ minHeight: '40px' }}
                        >
                          <Plus size={14} className="mr-1" /> Invite {entityName.slice(0, entityName.length - 1)}
                        </Button>
                      )}
                      actionCell={item => renderActionButtons(item)}
                    />
                  </div>
                )}
                {items.length > 0 && (
                  <AlertModal
                    show={toUpdateStatusOrDelete !== null}
                    onHide={() => setToUpdateStatusOrDelete(null)}
                    onDismissClick={() => setToUpdateStatusOrDelete(null)}
                    onContinueClick={updateStatusOrDeleteItem}
                    showProgress={deleteInProgress}
                    isEnabled={toUpdateStatusOrDelete?.isEnabled}
                    alertText={
                      toUpdateStatusOrDelete?.mode === 'ED'
                        ? `Are you sure you want to ${toUpdateStatusOrDelete?.isEnabled ? 'enable' : 'disable'
                        } this ${entityName?.toLowerCase().slice(0, entityName?.length - 1)}?`
                        : `Are you sure you want to delete this ${entityName?.toLowerCase()}? This action cannot be undone!`
                    }
                    progressText={
                      toUpdateStatusOrDelete?.mode === 'ED'
                        ? `${toUpdateStatusOrDelete?.isEnabled ? 'Enabling...' : 'Disabling...'}`
                        : `Deleting ${entityName?.toLowerCase()}...`
                    }
                  />
                )}
                {entityName === 'Users' && (
                  <AlertModal
                    show={userToReinvite !== null}
                    onHide={() => !reinviting && setUserToReinvite(null)}
                    onDismissClick={() => !reinviting && setUserToReinvite(null)}
                    onContinueClick={confirmReinviteUser}
                    dismissButtonText="Cancel"
                    continueButtonText="Send Invitation"
                    continueButtonVariant="primary"
                    showProgress={reinviting}
                    progressText="Sending invitation..."
                    alertText={
                      userToReinvite
                        ? `Do you want to send another invitation link to ${userToReinvite.name}?`
                        : 'Do you want to send another invitation link?'
                    }
                  />
                )}
                {entityName === 'Users' && (
                  <UserFormModal
                    show={itemActionMeta !== null}
                    user={itemActionMeta?.item || null}
                    roles={(() => {
                      const roleColumn = addFormRows?.[0]?.columns?.find(col => col.field?.key === 'role');
                      return roleColumn?.field?.options || [];
                    })()}
                    onHide={() => setItemActionMeta(null)}
                    onSubmit={handleCustomFormSubmit}
                    showProgress={itemAddingUpdating}
                    usersFromOrganization={usersFromOrganization}
                  />
                )}
                {entityName === 'Organizations' && (
                  <OrganizationFormModal
                    show={itemActionMeta !== null}
                    organization={itemActionMeta?.item || null}
                    users={requiredUsers}
                    onHide={() => setItemActionMeta(null)}
                    onSubmit={handleCustomFormSubmit}
                    showProgress={itemAddingUpdating}
                  />
                )}
                {entityName !== 'Users' && entityName !== 'Organizations' && (
                  <AddEditModal
                    entityName={entityName}
                    show={itemActionMeta !== null}
                    onHide={() => setItemActionMeta(null)}
                    toBeEditedItem={itemActionMeta ? itemActionMeta.item : null}
                    showProgress={itemAddingUpdating}
                    addForm={getForm({
                      entityName,
                      rows: addFormRows,
                      type: 'Add'
                    })}
                    updateForm={getForm({ entityName, rows: updateFormRows, type: 'Update' })}
                  />
                )}
              </>
            )
          )}
        </div>
      </div>
    </Container>
  );
};

export default EntityCRUD;
